import React from "react";
import * as Yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ComparisonTypeEnum } from "../../Enum";
import AutocompleteInput from "../AutocompleteInput/AutocompleteInput";
import PhaseSelector from "../PhaseSelector/PhaseSelector";

type HistoricalRuleFormProps = {
  onSubmit: (data: any) => void;
  manifestNames: Record<string, string>;
};

const HistoricalRuleForm: React.FC<HistoricalRuleFormProps> = ({
  onSubmit,
  manifestNames,
}) => {
  const validationSchema = Yup.object().shape({
    variableName: Yup.string().required("Variable name is required"),
    relatedVariablesPrefix: Yup.string()
      .required("Related variables prefix is required")
      .matches(/^\d+$/, "Must be a numeric prefix"),
    relatedCondition: Yup.number()
      .typeError("Condition must be selected")
      .required("Condition is required"),
    relatedValue: Yup.string().required("Related value is required"),
    errorMessage: Yup.string().required("Error message is required"),
    applicablePhaseIds: Yup.array().of(Yup.number()),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      variableName: "",
      relatedVariablesPrefix: "1",
      relatedCondition: 0,
      relatedValue: "",
      errorMessage: "",
      applicablePhaseIds: [],
    },
  });

  const selectedPhases = watch("applicablePhaseIds") as number[];

  const handleFormSubmit = (data: any) => {
    onSubmit(data);
    reset();
  };

  const handleReset = () => {
    reset();
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">
        Create Historical Rule
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Variable Name */}
        <Controller
          name="variableName"
          control={control}
          render={({ field }) => (
            <AutocompleteInput
              id="variableName"
              label="Variable"
              placeholder="Search for variable..."
              manifestNames={manifestNames}
              value={field.value}
              onChange={(name, id) => field.onChange(id)}
              error={errors.variableName?.message}
              required
            />
          )}
        />

        {/* Related Variables Prefix */}
        <div>
          <label
            htmlFor="relatedVariablesPrefix"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Related Variables Prefix <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="relatedVariablesPrefix"
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.relatedVariablesPrefix ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Enter numeric prefix (e.g., 1, 2, 3)..."
            {...register("relatedVariablesPrefix")}
          />
          {errors.relatedVariablesPrefix && (
            <p className="text-red-500 text-xs mt-1">
              {errors.relatedVariablesPrefix.message}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Prefix to identify related historical variables (numeric only)
          </p>
        </div>

        {/* Related Condition */}
        <div>
          <label
            htmlFor="relatedCondition"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Related Condition <span className="text-red-500">*</span>
          </label>
          <select
            id="relatedCondition"
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.relatedCondition ? "border-red-500" : "border-gray-300"
            }`}
            {...register("relatedCondition")}
          >
            <option value="">Select condition...</option>
            <option value={ComparisonTypeEnum.GreaterThan}>
              Greater Than (&gt;)
            </option>
            <option value={ComparisonTypeEnum.LessThan}>
              Less Than (&lt;)
            </option>
            <option value={ComparisonTypeEnum.EqualTo}>Equal To (=)</option>
            <option value={ComparisonTypeEnum.NotEqualTo}>
              Not Equal To (≠)
            </option>
            <option value={ComparisonTypeEnum.GreaterOrEqualTo}>
              Greater or Equal To (≥)
            </option>
            <option value={ComparisonTypeEnum.LessOrEqualTo}>
              Less or Equal To (≤)
            </option>
          </select>
          {errors.relatedCondition && (
            <p className="text-red-500 text-xs mt-1">
              {errors.relatedCondition.message}
            </p>
          )}
        </div>

        {/* Related Value */}
        <div>
          <label
            htmlFor="relatedValue"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Related Value <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="relatedValue"
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.relatedValue ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Enter value to compare against..."
            {...register("relatedValue")}
          />
          {errors.relatedValue && (
            <p className="text-red-500 text-xs mt-1">
              {errors.relatedValue.message}
            </p>
          )}
        </div>

        {/* Error Message */}
        <div className="md:col-span-2">
          <label
            htmlFor="errorMessage"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Error Message <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="errorMessage"
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.errorMessage ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Enter error message to display when rule fails..."
            {...register("errorMessage")}
          />
          {errors.errorMessage && (
            <p className="text-red-500 text-xs mt-1">
              {errors.errorMessage.message}
            </p>
          )}
        </div>

        {/* Applicable Phases */}
        <div className="md:col-span-2">
          <Controller
            name="applicablePhaseIds"
            control={control}
            render={({ field }) => (
              <PhaseSelector
                selectedPhases={selectedPhases || []}
                onChange={field.onChange}
                error={errors.applicablePhaseIds?.message}
              />
            )}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={handleReset}
          className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
        >
          Reset
        </button>
        <button
          type="submit"
          className="px-6 py-2.5 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-600 transition"
        >
          Create Rule
        </button>
      </div>
    </form>
  );
};

export default HistoricalRuleForm;